import { Component, computed, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';
import { InnogyComponentRendering } from '@core/jss-models';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import {
  type ComponentFields,
  getChildPlaceholder,
  type ImageField,
  type LinkField,
  type RenderingField,
  type RichTextField,
  type TextField,
} from '@sitecore-jss/sitecore-jss-angular';
import { hasBreadcrumbs } from '@core/jss-routing';
import { getDroplinkValue } from '@core/jss-utils';
import { getAppStoreButton, getGooglePlayButton } from '@core/navigation-store';
import {
  type Hero,
  HERO_FIELDS,
  HERO_TYPES,
  HeroComponent,
} from '@content/ui/hero';
import { computedContentSectionId } from '@core/content-section';

const HEADING_LINE1_FALLBACK: TextField = {
  value: 'HEADING LINE 1 IS REQUIRED',
};

@Component({
  selector: 'wl-hero-container',
  standalone: true,
  imports: [HeroComponent],
  templateUrl: './hero-container.component.html',
})
export class HeroContainerComponent {
  readonly #config = inject(ENVIRONMENT_CONFIG);
  readonly #store = inject(Store);

  readonly rendering = input.required<InnogyComponentRendering>();

  readonly id = computedContentSectionId(this.rendering);

  readonly hasBreadcrumbs = this.#store.selectSignal(hasBreadcrumbs);
  readonly appStoreButton = this.#store.selectSignal(getAppStoreButton);
  readonly googlePlayButton = this.#store.selectSignal(getGooglePlayButton);

  readonly hasAsset = computed(
    () =>
      this.rendering()?.componentName === HERO_TYPES.HERO_WITH_ASSET ||
      this.rendering()?.componentName === HERO_TYPES.HERO_WITH_APP_BUTTONS
  );
  readonly hasAppButtons = computed(
    () => this.rendering()?.componentName === HERO_TYPES.HERO_WITH_APP_BUTTONS
  );
  readonly hasWidget = computed(
    () => this.rendering()?.componentName === HERO_TYPES.HERO_WITH_WIDGET
  );

  readonly hasValidWidget = computed(() => {
    const rendering = this.rendering();
    return (
      this.hasWidget() &&
      getChildPlaceholder(rendering, 'widget-dynamic').length > 0
    );
  });

  readonly hero = computed((): Hero => {
    const rendering = this.rendering();
    const fields = rendering.fields;

    if (!fields) {
      return {
        headingLine1: HEADING_LINE1_FALLBACK,
      };
    }

    const get = <T>(field: HERO_FIELDS): T | undefined =>
      this.getField<T>(fields, field);

    return {
      headingLine1:
        get<TextField>(HERO_FIELDS.HEADING_LINE1) ?? HEADING_LINE1_FALLBACK,
      headingLine2: get<TextField>(HERO_FIELDS.HEADING_LINE2),
      primaryButton: get<LinkField>(HERO_FIELDS.PRIMARY_BUTTON),
      secondaryButton: get<LinkField>(HERO_FIELDS.SECONDARY_BUTTON),
      paragraph: get<TextField>(HERO_FIELDS.PARAGRAPH),
      richText: get<RichTextField>(HERO_FIELDS.RICH_TEXT),
      bottomImage: get<ImageField>(HERO_FIELDS.BOTTOM_IMAGE),
      bottomImageMobile: get<ImageField>(HERO_FIELDS.BOTTOM_IMAGE_MOBILE),
      useBottomImageOnMobile: get<RenderingField<boolean>>(
        HERO_FIELDS.USE_BOTTOM_IMAGE_ON_MOBILE
      )?.value,
      mainImage: this.hasValidWidget()
        ? undefined
        : get<ImageField>(HERO_FIELDS.MAIN_IMAGE),
      backgroundImage: {
        image: get<ImageField>(HERO_FIELDS.BACKGROUND_IMAGE),
        position: getDroplinkValue(
          fields,
          HERO_FIELDS.BACKGROUND_IMAGE_POSITION
        ),
        repeat: getDroplinkValue(fields, HERO_FIELDS.BACKGROUND_IMAGE_REPEAT),
        size: getDroplinkValue(fields, HERO_FIELDS.BACKGROUND_IMAGE_SIZE),
        useOnMobile: get<RenderingField<boolean>>(
          HERO_FIELDS.USE_BACKGROUND_IMAGE_ON_MOBILE
        )?.value,
      },
      backgroundImageMobile: {
        image: get<ImageField>(HERO_FIELDS.BACKGROUND_IMAGE_MOBILE),
        position: getDroplinkValue(
          fields,
          HERO_FIELDS.BACKGROUND_IMAGE_MOBILE_POSITION
        ),
        repeat: getDroplinkValue(
          fields,
          HERO_FIELDS.BACKGROUND_IMAGE_MOBILE_REPEAT
        ),
        size: getDroplinkValue(
          fields,
          HERO_FIELDS.BACKGROUND_IMAGE_MOBILE_SIZE
        ),
      },
      widget: this.hasValidWidget() ? rendering : undefined,
      appStoreButton: this.hasAppButtons()
        ? (this.appStoreButton() ?? undefined)
        : undefined,
      googlePlayButton: this.hasAppButtons()
        ? (this.googlePlayButton() ?? undefined)
        : undefined,
    };
  });

  readonly enableEssentRebranding =
    this.#config.styles?.enableEssentRebranding || false;

  private getField<T>(
    fields: ComponentFields,
    field: HERO_FIELDS
  ): T | undefined {
    return fields[field] as T | undefined;
  }
}
